import { FC, useCallback, useMemo } from 'react';
import { useGetContactQuery } from '../../store/generated/generatedApi';
import { FormControl, FormLabel, Grid, FormHelperText } from '@mui/material';
import { ContactDto } from '../../dtos';
import { emptyGuid } from '../../util';
import { ContactQuickAddButton } from '../QuickAdds';
import { IEntityAutocomplete } from './IEntityAutocomplete';
import { ContactQuickEditButton } from '../QuickAdds/ContactQuickEditButton';

export interface IContactSelectProps {
    label?: string;
    selectedContactId: string;
    handleSelectedContactChange: (selectedContact?: ContactDto) => void;
    required?: boolean;
    errorMessage?: string;
    disabled?: boolean;
    customerId?: string;
    siteId?: string;
    disableAdd?: boolean;
    disableEdit?: boolean;
}

export const ContactSelect: FC<IContactSelectProps> = (props) => {
    const { label = 'Contact', customerId, siteId, selectedContactId, handleSelectedContactChange, required, errorMessage, disabled, disableAdd, disableEdit } = props;
    const { data: contacts, isLoading: isLoadingContacts } = useGetContactQuery({
        customerId,
        siteId,
        sorting: ['CONTACT_NAME+'],
        page: 0,
        pageSize: 10000,
        includeInactive: false,
    });

    const handleSelectChange = useCallback(
        (value: ContactDto | null) => {
            handleSelectedContactChange(value ?? undefined);
        },
        [handleSelectedContactChange]
    );

    const selectedContact = useMemo(() => {
        if (selectedContactId === '' || selectedContactId === emptyGuid) {
            return null;
        }
        return contacts?.pageResults.find((x) => x.id === selectedContactId) ?? null;
    }, [contacts?.pageResults, selectedContactId]);

    const handleNewContactAdded = useCallback(
        (newContact: ContactDto) => {
            handleSelectChange(newContact);
        },
        [handleSelectChange]
    );

    const contactOptions = useMemo(() => {
        return contacts?.pageResults || [];
    }, [contacts?.pageResults]);

    return (
        <Grid item container alignItems='center'>
            <Grid item xs={disableAdd ? 12 : 11}>
                <FormControl fullWidth required={required} disabled={disabled}>
                    <FormLabel>{label}</FormLabel>
                    <IEntityAutocomplete
                        options={contactOptions}
                        onChange={(e, value) => {
                            handleSelectChange(value ?? null);
                        }}
                        value={selectedContact}
                        getOptionLabel={(option: ContactDto) =>
                            option.firstName +
                            (option.lastName ? ' ' + option.lastName : '') +
                            (option.title ? ' - ' + option.title : '') +
                            (option.cellPhone ? ' - ' + option.cellPhone : '')
                        }
                        disabled={disabled}
                        isLoading={isLoadingContacts}
                    />
                    <FormHelperText>{errorMessage}</FormHelperText>
                </FormControl>
            </Grid>
            {!disableAdd && !selectedContact && (
                <Grid item xs={1} pt={2}>
                    <ContactQuickAddButton onAdd={handleNewContactAdded} customerId={customerId} disabled={disabled} />
                </Grid>
            )}
            {!disableEdit && selectedContact && (
                <Grid item xs={1} pt={2}>
                    <ContactQuickEditButton onEdit={handleNewContactAdded} customerId={customerId} contact={selectedContact} disabled={disabled} />
                </Grid>
            )}
        </Grid>
    );
};
